// Cash balance + 401(k)/profit-sharing COMBO scenario solver, built on the
// validated DB benefit-limit engine (compliance/limits415b.js) and combined DB/DC testing layer
// (compliance/combinedDbDc.js).
//
// The proven deterministic design path is unchanged from the parity-verified
// engine:
//   1. Owner cash-balance credit: the level annual pay credit accumulating
//      (at the plan interest credit) to the §415(b) maximum lump sum at the
//      owner's retirement age over the funding period.
//   2. Staff cash-balance credit: the given flat percent, stepped up in
//      half-point increments only if §401(a)(26) needs more people at a
//      meaningful benefit.
//   3. Staff profit sharing: closed-form to the combined gateway (required
//      rate − averaged/own NHCE DB rate − existing nonelective), then whole
//      basis-point steps while the aggregate rate groups, MVAR groups, or
//      ABPT still need more (all monotone in the staff DC rate).
//
// On top of that path this module produces the comparison scenarios a combo
// proposal needs — lowest-cost passing, the 7.5% deemed-gateway posture, a
// reduced-owner-credit variant, a more-through-DC variant, and a budget fit —
// each validated through the SAME complete combined pipeline and wrapped in
// the shared compliance status contract. Deterministic: same census and
// facts in, same scenarios out. MODELED, scenario-grade; not a substitute
// for the plan's enrolled actuary.

import { evaluateCombinedDbDc, normalizeDbDcAggregationFacts, requiredAggregateGatewayPercent } from './compliance/combinedDbDc.js'
import { maxAnnualCashBalanceCredit, solveMaxCashBalanceCredit, limits415bAssumptions, dollarLimit415bForYear } from './compliance/limits415b.js'
import { buildSection415Schedule } from './compliance/annualAdditions.js'
import { evaluateDeduction404Screen, evaluateCombinedDeduction404a7Screen } from './compliance/deductionScreen.js'
import {
  combineComplianceStatuses,
  computeInputFingerprint,
  makeAssumption,
  makeComplianceStatus,
  makeLimitation,
  makeReason,
  NEW_COMP_CALCULATION_VERSION,
} from './compliance/complianceStatus.js'
import { readinessDisclosures, toCents } from './newCompInputs.js'
import { NEW_COMP_MODELING_DEFAULTS, SCENARIO_SEARCH_LIMITS } from './modelingDefaults.js'
import { DEFAULT_ACTUARIAL_SET } from './compliance/actuarialAssumptions.js'

const ROLE_TARGET = 'target'
const ROLE_NON_TARGET_HCE = 'non-target-hce'
const ROLE_STAFF = 'staff'

const roundCents = (value) => Math.round((Number(value) || 0) * 100) / 100
const roundBp = (percent) => Math.round(percent * 100) / 100

// §416 top-heavy minimum for a DB/DC combination.
//
// Two things move from the DC-only rule. The "lesser of 3% or the highest key
// employee rate" exception in §416(c)(2)(B) does not apply when the DC plan
// sits in a required aggregation group that lets the DB plan satisfy
// §401(a)(4) or §410(b) — which is exactly what a combo design is. And for a
// non-key participant covered under BOTH plans, the anti-duplication safe
// harbor of §1.416-1 M-12 substitutes a 5% DC minimum for the DB accrual; a
// participant in the DC plan alone stays at 3%.
//
// The other M-12 routes (a 2% DB minimum accrual, or proving comparability)
// are often cheaper, but whether a given pay credit produces a 2% accrual of
// high-3 average pay per year of service is an actuarial question this model
// cannot answer, so they are disclosed rather than assumed. Cash-balance
// credits never count toward the DC minimum: crediting DB money against it is
// the duplication M-12 exists to prevent.
//
// `staffShortfallCents` is the portion a uniform staff DC rate can close. The
// scenario search gates on that alone, because a non-key NON-TARGET HCE is
// driven by its own rate and no staff rate can repair it; that shortfall still
// reports through the scenario's top-heavy status with the employee named.
const TOP_HEAVY_DC_ONLY_PERCENT = 3
const TOP_HEAVY_DB_DC_PERCENT = 5

export function evaluateComboTopHeavy(people = [], rows = [], planFacts = {}) {
  const facts = planFacts.topHeavy || { status: 'unknown' }
  if (facts.status === 'not-top-heavy') {
    return {
      applicable: false, status: 'not-applicable', modeledMinimumPercent: 0,
      shortfallCents: 0, staffShortfallCents: 0, rows: [],
    }
  }
  if (facts.status !== 'top-heavy') {
    return {
      applicable: false, status: 'requires-review', unknown: true,
      modeledMinimumPercent: null, shortfallCents: null, staffShortfallCents: 0, rows: [],
    }
  }
  const peopleById = new Map(people.map((person) => [person.id, person]))
  const shortfallRows = []
  let shortfallCents = 0
  let staffShortfallCents = 0
  let modeledMinimumPercent = 0
  for (const row of rows) {
    const person = peopleById.get(row.id)
    if (!person || person.isKey || row.excludable || person.eligibilityStatus !== 'eligible') continue
    const requiredPercent = row.cbCredit > 0 ? TOP_HEAVY_DB_DC_PERCENT : TOP_HEAVY_DC_ONLY_PERCENT
    modeledMinimumPercent = Math.max(modeledMinimumPercent, requiredPercent)
    // §415(c)(3) compensation, and DC employer money only.
    const requiredCents = Math.round(toCents(row.gatewayComp) * requiredPercent / 100)
    const providedCents = toCents(row.psAmount) + toCents(row.nonelective)
      + toCents(row.qnec) + toCents(row.forfeiture) + toCents(row.match)
    const shortfall = Math.max(0, requiredCents - providedCents)
    if (shortfall > 0) {
      shortfallRows.push({
        id: row.id, role: row.role, requiredPercent, requiredCents, providedCents,
        shortfallCents: shortfall,
      })
      shortfallCents += shortfall
      if (row.role === ROLE_STAFF) staffShortfallCents += shortfall
    }
  }
  return {
    applicable: true,
    status: shortfallCents > 0 ? 'fails' : 'passes',
    assumed: facts.source === 'assumed',
    modeledMinimumPercent,
    shortfallCents,
    staffShortfallCents,
    rows: shortfallRows,
  }
}

const topHeavyBlocks = (tests) => (tests?.topHeavy?.staffShortfallCents ?? 0) > 0

export function solveCashBalanceComboScenarios({
  participants = [],
  planFacts = {},
  cbFacts = {},
  goal = {},
  options = {},
  readiness = null,
  reportInputs = {},
} = {}) {
  const aggregationFacts = normalizeDbDcAggregationFacts(cbFacts.aggregationFacts)
  const interestCredit = Number(cbFacts.interestCreditRate ?? NEW_COMP_MODELING_DEFAULTS.cashBalanceInterestCreditRate)
  const fundingYears = Math.max(1, Math.round(cbFacts.fundingYears ?? NEW_COMP_MODELING_DEFAULTS.ownerFundingYears))
  const dollarLimit415b = Number(cbFacts.dollarLimit415b)
    || dollarLimit415bForYear(planFacts.planYear) || null
  const targetIdSet = new Set(goal.targetIds || [])

  const base = {
    status: null,
    goal: { objective: 'combo-maximize-targets', targetIds: [...targetIdSet], budget: goal.budget ?? null },
    scenarios: [],
    diagnostics: null,
    readiness,
    calculationVersion: NEW_COMP_CALCULATION_VERSION,
    rulesYear: planFacts.planYear ?? null,
    inputFingerprint: null,
    planFacts: { ...planFacts, dbDcAggregation: { ...aggregationFacts } },
    solverFacts: {
      fundingYears,
      interestCreditRate: interestCredit,
      dbPbgcCovered: cbFacts.dbPbgcCovered ?? 'unknown',
      aggregationFacts: { ...aggregationFacts },
      assumptionSet: cbFacts.assumptionSet ?? DEFAULT_ACTUARIAL_SET,
    },
    reportInputs: reportInputs && typeof reportInputs === 'object' ? { ...reportInputs } : {},
  }
  const blocked = (code, message) => ({
    ...base,
    status: 'blocked',
    blockedStatus: makeComplianceStatus({ status: 'unavailable', reasons: [makeReason(code, message)] }),
  })

  if (!aggregationFacts.aggregate) {
    return blocked('aggregation-not-confirmed',
      'Confirm that the cash-balance and DC components are being tested together (permissive aggregation). Plans are never aggregated automatically.')
  }
  if (!(dollarLimit415b > 0)) {
    return blocked('missing-415b-limit', 'The §415(b) dollar limit for this plan year is unavailable.')
  }
  if (!Number.isFinite(interestCredit) || interestCredit < 0) {
    return blocked('invalid-interest-credit', 'The cash-balance interest credit rate is invalid.')
  }

  const people = (Array.isArray(participants) ? participants : [])
    .filter((p) => p && (Number(p.allocationCompensation) > 0 || !p.nonexcludableForCoverage))
    .map((p) => ({
      ...p,
      comp: Number(p.allocationCompensation) || 0,
      role: targetIdSet.has(p.id) ? ROLE_TARGET : (p.isHCE ? ROLE_NON_TARGET_HCE : ROLE_STAFF),
      testingAgeResolved: Number(p.testingAge) || 65,
      excludable: !p.nonexcludableForCoverage,
    }))
  const staff = people.filter((p) => p.role === ROLE_STAFF && !p.excludable && p.comp > 0)
  const hces = people.filter((p) => p.isHCE && !p.excludable)
  if (!people.length || !staff.length || !hces.length || !targetIdSet.size) {
    return blocked(
      !people.length ? 'no-participants' : !targetIdSet.size ? 'no-targets' : !hces.length ? 'no-hce' : 'no-staff',
      !people.length ? 'The census has no employees.'
        : !targetIdSet.size ? 'Pick at least one owner to design around.'
          : !hces.length ? 'No HCE or owner was identified.' : 'A combo design needs at least one eligible staff member.',
    )
  }

  // Existing DC annual additions must already fit §415(c).
  const overCap = people.find((p) => !p.excludable && p.comp > 0
    && (p.deferralCents + p.matchCents + p.otherNonelectiveCents + p.safeHarborNonelectiveCents
      + p.qnecCents + p.forfeitureCents + p.afterTaxCents)
      > Math.min(p.limit415cCents, toCents(Number.isFinite(Number(p.section415Compensation))
        ? Number(p.section415Compensation) : p.comp)) + 0)
  if (overCap) {
    return {
      ...base,
      status: 'infeasible',
      diagnostics: {
        bindingConstraint: '415c-prior-additions',
        message: `Annual additions already entered for ${overCap.name || overCap.id} exceed the §415(c) limit before any proposed contribution.`,
        participantId: overCap.id,
      },
    }
  }

  const targetPsPercent = Math.max(0, Number(options.targetPsPercent ?? NEW_COMP_MODELING_DEFAULTS.ownerProfitSharingPercent))
  const nonTargetHceCbPercent = Math.max(0, Number(options.nonTargetHceCbPercent ?? NEW_COMP_MODELING_DEFAULTS.nonTargetHceCashBalancePercent))
  const nonTargetHcePsPercent = Math.max(0, Number(options.nonTargetHcePsPercent ?? 0))
  const maxStaffCbPercent = Math.max(0, Number(options.maxStaffCbPercent ?? NEW_COMP_MODELING_DEFAULTS.maxStaffCashBalancePercent))
  const maxStaffPsPercent = Math.max(0, Number(options.maxStaffPsPercent ?? NEW_COMP_MODELING_DEFAULTS.maxComboStaffProfitSharingPercent))

  base.inputFingerprint = computeInputFingerprint({
    goal: base.goal,
    aggregationFacts,
    interestCredit,
    fundingYears,
    dollarLimit415b,
    options: { targetPsPercent, nonTargetHceCbPercent, nonTargetHcePsPercent, maxStaffCbPercent, maxStaffPsPercent, staffCbPercent: options.staffCbPercent ?? NEW_COMP_MODELING_DEFAULTS.staffCashBalancePercent },
    participants: people.map((p) => ({
      id: p.id, isHCE: p.isHCE, age: p.age, comp: p.comp,
      testingAge: p.testingAgeResolved, headroom: p.remaining415cHeadroomCents,
    })),
  })

  // ---- Target cash-balance credits (§415(b) level-credit solve) ----
  const targetSolves = {}
  for (const person of people) {
    if (person.role !== ROLE_TARGET) continue
    const explicit = options.targetCredits?.[person.id]
    if (Number(explicit) > 0) {
      targetSolves[person.id] = { status: 'given', suggestedCredit: Number(explicit), levelCredit: Number(explicit) }
      continue
    }
    const solve = solveMaxCashBalanceCredit({
      comp: person.comp,
      high3: person.high3 ?? null,
      retirementAge: person.retirementAge ?? person.testingAgeResolved,
      fundingYears,
      serviceYears: person.serviceYears ?? 0,
      interestCredit,
      dollarLimit415b,
      planAprTable: options.planAprTable,
    })
    if (solve.status !== 'solved') {
      return blocked(solve.reason || 'unavailable', `The §415(b) level-credit solve is unavailable for ${person.name || person.id} (${solve.reason}).`)
    }
    // The level solve targets the limit AT RETIREMENT; the year-one accrued
    // benefit is separately capped by the participant's CURRENT permitted
    // benefit. When the level credit exceeds the first-year maximum, cap it
    // (rounded down to $100) and disclose — never present an over-limit
    // credit as a clean design.
    const firstYearMax = maxAnnualCashBalanceCredit({
      age: person.age,
      comp: person.comp,
      high3: person.high3 ?? null,
      participationYears: 1,
      serviceYears: person.serviceYears ?? 1,
      dollarLimit415b,
    })
    if (firstYearMax.status === 'ok' && solve.suggestedCredit > firstYearMax.maxCredit) {
      targetSolves[person.id] = {
        ...solve,
        suggestedCredit: Math.floor(firstYearMax.maxCredit / 100) * 100,
        limitedByFirstYear415b: true,
        firstYearMaxCredit: firstYearMax.maxCredit,
      }
    } else {
      targetSolves[person.id] = solve
    }
  }

  const buildRows = (staffCbBp, staffPsBp, { targetCreditScaleBp = 10000, targetPsFill = false } = {}) =>
    people.map((person) => {
      const cbCredit = person.role === ROLE_TARGET
        ? roundCents(targetSolves[person.id].suggestedCredit * targetCreditScaleBp / 10000)
        : roundCents(person.comp * (person.role === ROLE_NON_TARGET_HCE ? nonTargetHceCbPercent * 100 : staffCbBp) / 10000)
      const otherNonelective = person.otherNonelectiveCents / 100
      const safeHarborNonelective = person.safeHarborNonelectiveCents / 100
      const nonelective = otherNonelective + safeHarborNonelective
      const qnec = person.qnecCents / 100
      const forfeiture = person.forfeitureCents / 100
      const deferral = person.deferralCents / 100
      const match = person.matchCents / 100
      const headroom = Math.max(0, Math.min(
        person.limit415cCents / 100,
        person.section415Compensation || person.comp)
        - deferral - match - nonelective - qnec - forfeiture - person.afterTaxCents / 100)
      const requestedPsAmount = person.role === ROLE_TARGET && targetPsFill
        ? headroom
        : roundCents(person.comp * (
          person.role === ROLE_TARGET ? targetPsPercent * 100
            : person.role === ROLE_NON_TARGET_HCE ? nonTargetHcePsPercent * 100 : staffPsBp
        ) / 10000)
      const psAmount = Math.min(requestedPsAmount, headroom)
      return {
        id: person.id,
        role: person.role,
        isHCE: person.isHCE,
        excludable: person.excludable,
        comp: person.comp,
        gatewayComp: person.section415Compensation || person.comp,
        age: person.age,
        testingAge: person.testingAgeResolved,
        cbCredit,
        cbOpeningBalance: (person.cashBalanceOpeningBalanceCents || 0) / 100,
        psAmount,
        requestedPsAmount,
        otherNonelective,
        safeHarborNonelective,
        nonelective,
        qnec,
        forfeiture,
        deferral,
        match,
        dcAllocation: nonelective + qnec + forfeiture + psAmount,
        abptDcAllocation: nonelective + qnec + forfeiture + psAmount + deferral + match,
      }
    })

  let evaluations = 0
  const runTests = (rows) => {
    evaluations += 1
    const result = evaluateCombinedDbDc(rows, {
      aggregationFacts,
      interestCredit,
      planAprTable: options.planAprTable,
      testingAprTable: options.testingAprTable,
      planYear: planFacts.planYear,
      assumptionSet: cbFacts.assumptionSet,
    })
    return { ...result, topHeavy: evaluateComboTopHeavy(people, rows, planFacts) }
  }
  const isPassing = (tests) => tests.available && tests.status === 'passes'

  // ---- Solve one design path for a given target-credit scale ----
  const solveDesign = ({ targetCreditScaleBp = 10000, targetPsFill = false, forceGatewayPercent = null } = {}) => {
    let staffCbBp = Math.round(Math.max(0, Number(options.staffCbPercent ?? NEW_COMP_MODELING_DEFAULTS.staffCashBalancePercent)) * 100)
    let probe = runTests(buildRows(staffCbBp, 0, { targetCreditScaleBp, targetPsFill }))
    if (!probe.available) return { status: 'unavailable', reason: probe.reason, tests: probe }
    if (!probe.mostValuable.available) {
      return { status: 'unavailable', reason: probe.mostValuable.reason, tests: probe }
    }
    while (!probe.minimumParticipation.passes && staffCbBp < maxStaffCbPercent * 100
      && evaluations < SCENARIO_SEARCH_LIMITS.maxMinimumParticipationEvaluations) {
      staffCbBp += 50
      probe = runTests(buildRows(staffCbBp, 0, { targetCreditScaleBp, targetPsFill }))
    }
    if (!probe.minimumParticipation.passes) {
      return { status: 'infeasible', binding: '401a26', tests: probe, staffCbBp }
    }
    // Closed-form to the gateway, then an exact binary search to the basis
    // point for the aggregate rate groups / MVAR / ABPT — every one of those
    // is monotone in the staff DC rate (raising it only lifts NHCE rates).
    const requiredPercent = forceGatewayPercent ?? probe.requiredGatewayPercent ?? 0
    const staffById = new Map(staff.map((person) => [person.id, person]))
    // Uniform DC profit sharing must close the largest participant-specific
    // shortfall. Subtracting unrelated minima can overstate the starting rate
    // when the smallest DB and existing-NEC rates occur on different people.
    const gatewayNeededPercent = Math.max(0, ...probe.gatewayRows.map((row) => {
      const person = staffById.get(row.id)
      if (!person || !(person.comp > 0)) return 0
      const existingNonelectivePercent = ((
        person.otherNonelectiveCents + person.safeHarborNonelectiveCents
        + person.qnecCents + person.forfeitureCents
      ) / 100 / person.comp) * 100
      return roundBp(requiredPercent - row.dbGatewayRatePercent - existingNonelectivePercent)
    }))
    const maxBp = Math.round(maxStaffPsPercent * 100)
    // The §416 minimum is monotone in the staff DC rate (raising it only lifts
    // non-key employer money, subject to the §415(c) headroom clamp in
    // buildRows), so gating here keeps the basis-point search exact.
    const passesHere = (t) => !topHeavyBlocks(t) && (forceGatewayPercent === null
      ? isPassing(t)
      : (isPassing(t) && t.gatewayRows.every((r) => r.gatewayRatePercent >= forceGatewayPercent)))
    const testsAt = (bp) => runTests(buildRows(staffCbBp, bp, { targetCreditScaleBp, targetPsFill }))
    let staffPsBp = Math.min(Math.round(gatewayNeededPercent * 100), maxBp)
    let tests = testsAt(staffPsBp)
    if (tests.available && !passesHere(tests) && staffPsBp < maxBp) {
      const atMax = testsAt(maxBp)
      if (!passesHere(atMax)) {
        return { status: 'infeasible', binding: bindingFrom(atMax), tests: atMax, staffCbBp, staffPsBp: maxBp }
      }
      let lo = staffPsBp
      let hi = maxBp
      while (hi - lo > 1 && evaluations < SCENARIO_SEARCH_LIMITS.maxEvaluations) {
        const mid = Math.floor((lo + hi) / 2)
        if (passesHere(testsAt(mid))) hi = mid
        else lo = mid
      }
      staffPsBp = hi
      tests = testsAt(hi)
    }
    if (!tests.available || !passesHere(tests)) {
      return { status: 'infeasible', binding: bindingFrom(tests), tests, staffCbBp, staffPsBp }
    }
    return { status: 'solved', staffCbBp, staffPsBp, tests, targetCreditScaleBp, targetPsFill }
  }

  const bindingFrom = (tests) => {
    if (!tests?.available) return 'inputs'
    if (!tests.minimumParticipation?.passes) return '401a26'
    if (!tests.gatewaySatisfied) return 'aggregate-gateway'
    if (topHeavyBlocks(tests)) return 'top-heavy'
    if (!tests.abptPass) return 'abpt'
    if (!tests.allRateGroupsPass) return 'rate-group'
    if (tests.mostValuable?.available && !tests.mostValuable.allRateGroupsPass) return 'most-valuable-rate-group'
    return 'unknown'
  }

  // ---- Scenario assembly ----
  const scenarioFrom = (id, label, solved, explanation, { optimality = 'exact-minimum' } = {}) => {
    const rows = buildRows(solved.staffCbBp, solved.staffPsBp, {
      targetCreditScaleBp: solved.targetCreditScaleBp ?? 10000,
      targetPsFill: solved.targetPsFill ?? false,
    })
    const tests = solved.tests
    const testRowById = new Map((tests.rows || []).map((row) => [row.id, row]))
    const psCentsById = Object.fromEntries(rows.map((row) => [row.id, toCents(row.psAmount)]))
    const cbCentsById = Object.fromEntries(rows.map((row) => [row.id, toCents(row.cbCredit)]))
    const schedule = buildSection415Schedule(people, { proposedProfitSharingCentsById: psCentsById })
    const deduction404a3 = evaluateDeduction404Screen(people, { proposedProfitSharingCentsById: psCentsById })
    const deduction404a7 = evaluateCombinedDeduction404a7Screen(people, {
      proposedProfitSharingCentsById: psCentsById,
      cashBalanceCreditCentsById: cbCentsById,
      dbPbgcCovered: cbFacts.dbPbgcCovered ?? 'unknown',
    })

    const perPerson = rows.map((row) => {
      const maxCb = maxAnnualCashBalanceCredit({
        age: row.age,
        comp: row.comp,
        high3: people.find((p) => p.id === row.id)?.high3 ?? null,
        participationYears: 1,
        serviceYears: people.find((p) => p.id === row.id)?.serviceYears ?? 1,
        dollarLimit415b,
      })
      return {
        id: row.id,
        role: row.role,
        isHCE: row.isHCE,
        compensationCents: toCents(row.comp),
        cbCreditCents: toCents(row.cbCredit),
        profitSharingCents: toCents(row.psAmount),
        profitSharingRequestedCents: toCents(row.requestedPsAmount),
        profitSharingReducedCents: Math.max(0, toCents(row.requestedPsAmount) - toCents(row.psAmount)),
        safeHarborNonelectiveCents: toCents(row.safeHarborNonelective),
        otherNonelectiveCents: toCents(row.otherNonelective),
        nonelectiveCents: toCents(row.nonelective),
        qnecCents: toCents(row.qnec),
        forfeitureCents: toCents(row.forfeiture),
        matchCents: toCents(row.match),
        deferralCents: toCents(row.deferral),
        employerCents: toCents(row.cbCredit) + toCents(row.psAmount) + toCents(row.nonelective)
          + toCents(row.qnec) + toCents(row.match),
        accrualRatePercent: testRowById.get(row.id)?.accrualRatePercent ?? null,
        aggregateEbar: testRowById.get(row.id)?.aggregateEbar ?? null,
        mostValuableEbar: testRowById.get(row.id)?.mostValuableEbar ?? null,
        maxCbCreditCents: maxCb.status === 'ok' ? toCents(maxCb.maxCredit) : null,
        cbExceedsMaxCredit: maxCb.status === 'ok' && toCents(row.cbCredit) > toCents(maxCb.maxCredit) + 1,
      }
    })
    const targets = perPerson.filter((row) => row.role === ROLE_TARGET)
    const employerCostCents = perPerson.reduce((sum, row) => sum + row.employerCents, 0)
    const cbTotalCents = perPerson.reduce((sum, row) => sum + row.cbCreditCents, 0)

    // §415(b): the level solve keeps solved credits inside the limit; explicit
    // target credits (or scales > 100%) are checked per person.
    const overMax = perPerson.filter((row) => row.cbExceedsMaxCredit)
    const section415bStatus = makeComplianceStatus({
      status: overMax.length ? 'fails' : 'passes',
      reasons: overMax.length
        ? [makeReason('cb-credit-over-415b-max',
            'A cash-balance credit exceeds the modeled §415(b) first-year maximum for that participant.',
            { employeeIds: overMax.map((row) => row.id) })]
        : [],
    })
    const topHeavy = tests.topHeavy || evaluateComboTopHeavy(people, rows, planFacts)
    const topHeavyStatus = makeComplianceStatus({
      status: topHeavy.status,
      reasons: topHeavy.status === 'fails'
        ? [makeReason('top-heavy-minimum-shortfall',
            `Non-key participants are short $${(topHeavy.shortfallCents / 100).toFixed(2)} of the ${topHeavy.modeledMinimumPercent}% §416 minimum for a DB/DC aggregation group.`,
            { employeeIds: topHeavy.rows.map((row) => row.id) })]
        : [],
      assumptions: topHeavy.assumed
        ? [makeAssumption('top-heavy-assumed', 'Top-heavy status assumed', 'top-heavy', { source: 'user', estimated: true })]
        : [],
      limitations: topHeavy.unknown
        ? [makeLimitation('top-heavy-unknown',
            'Top-heavy status is unknown, so the §416 minimum for the aggregation group is not modeled.')]
        : topHeavy.applicable
          ? [makeLimitation('top-heavy-db-dc-route',
              'The §416 minimum is modeled on the 5% DC safe harbor of §1.416-1 M-12 for participants covered by both plans. A 2% DB minimum accrual or a comparability demonstration may cost less; both depend on the adopted accrual structure and need the actuary.')]
          : [],
    })
    const statusParts = {
      minimumParticipation: makeComplianceStatus({
        status: tests.minimumParticipation.passes ? 'passes' : 'fails',
        reasons: tests.minimumParticipation.passes ? [] : [makeReason('401a26',
          `Only ${tests.minimumParticipation.benefitingCount} of the required ${tests.minimumParticipation.requiredBenefiting} participants receive a meaningful DB benefit.`)],
      }),
      combinedGateway: makeComplianceStatus({
        status: !tests.gatewayApplicable ? 'not-applicable' : tests.gatewaySatisfied ? 'passes' : 'fails',
        assumptions: aggregationFacts.nhceDbAveragingElection
          ? [makeAssumption('nhce-db-averaging', 'NHCE DB equivalent-rate averaging elected', true, { source: 'user' })]
          : [],
        limitations: aggregationFacts.gatewayEscapesUnknown
          ? [makeLimitation('gateway-escapes-unknown',
              'Primarily-DB character and broadly-available-separate-plans status are unanswered; the aggregate gateway was applied (the conservative posture).')]
          : [],
      }),
      rateGroups: makeComplianceStatus({ status: tests.allRateGroupsPass ? 'passes' : 'fails' }),
      topHeavy: topHeavyStatus,
      mostValuable: makeComplianceStatus({
        status: !tests.mostValuable.available ? 'unavailable' : tests.mostValuable.allRateGroupsPass ? 'passes' : 'fails',
      }),
      abpt: makeComplianceStatus({ status: tests.abptPass ? 'passes' : 'fails' }),
      section415b: section415bStatus,
      section415c: makeComplianceStatus({ status: schedule.status, reasons: schedule.reasons }),
      deduction: makeComplianceStatus({
        status: deduction404a3.status,
        reasons: deduction404a3.reasons,
        limitations: deduction404a3.limitations,
      }),
      deduction404a7: makeComplianceStatus({
        status: deduction404a7.status,
        reasons: deduction404a7.reasons,
        assumptions: deduction404a7.assumptions,
        limitations: deduction404a7.limitations,
      }),
    }
    const disclosures = readiness ? readinessDisclosures(readiness, planFacts) : { assumptions: [], limitations: [] }
    const actuarialLimitations = (tests.assumptions?.limitations || [])
      .map((message, index) => makeLimitation(`combined-scope-${index}`, message))
    const estimatedFactors = tests.assumptions?.estimated?.length
      ? [makeLimitation('estimated-apr-factors',
          `${tests.assumptions.estimated.length} annuity factor(s) used here are interpolated or estimated rather than proposal-verified; an actuary must confirm them.`)]
      : []
    const status = combineComplianceStatuses([
      ...Object.values(statusParts),
      makeComplianceStatus({
        // Combined DB/DC results are permanently review-gated. Even exact
        // table factors do not turn a modeled proposal into an actuarial or
        // formal nondiscrimination determination.
        status: 'requires-review',
        assumptions: [
          ...disclosures.assumptions,
          makeAssumption('actuarial-assumption-set', 'DB conversions use the versioned scenario APR tables',
            cbFacts.assumptionSet ?? DEFAULT_ACTUARIAL_SET, { source: 'modeled', estimated: true }),
        ],
        limitations: [...disclosures.limitations, ...actuarialLimitations, ...estimatedFactors,
          makeLimitation('actuary-review',
            'DB conversion assumptions, §430 funding, and the adopted actuarial-equivalence basis must be confirmed by the plan’s enrolled actuary before reliance.')],
      }),
    ], { rulesYear: planFacts.planYear ?? null, inputFingerprint: base.inputFingerprint })

    const targetPeople = people.filter((p) => p.role === ROLE_TARGET)
    const targetLabelBase = options.targetGroupLabel || 'Owner'
    const targetLabels = new Map(targetPeople.map((p, index) => [
      p.id,
      targetPeople.length === 1 ? targetLabelBase : `${targetLabelBase} ${index + 1}`,
    ]))
    const hceLabel = options.nonTargetHceGroupLabel || 'Non-Owner HCE'
    const staffLabel = options.staffGroupLabel || ''
    return {
      id,
      label,
      strategy: id,
      optimality,
      status,
      staffCbPercent: solved.staffCbBp / 100,
      staffPsPercent: solved.staffPsBp / 100,
      // Alias for surfaces (proposal snapshots, cards) that read the DC rate.
      staffPercent: solved.staffPsBp / 100,
      targetSolves,
      perPerson,
      totals: {
        employerCostCents,
        cashBalanceCents: cbTotalCents,
        profitSharingCents: perPerson.reduce((sum, row) => sum + row.profitSharingCents, 0),
        targetCashBalanceCents: targets.reduce((sum, row) => sum + row.cbCreditCents, 0),
        targetProfitSharingCents: targets.reduce((sum, row) => sum + row.profitSharingCents, 0),
        targetSharePercent: employerCostCents > 0
          ? (targets.reduce((sum, row) => sum + row.employerCents, 0) / employerCostCents) * 100
          : 0,
      },
      tests: statusParts,
      detail: {
        combined: tests,
        schedule415c: schedule,
        deduction404a3,
        deduction404a7,
      },
      explanation,
      assumptions: status.assumptions,
      limitations: status.limitations,
      suggestedCashBalance: {
        interestCreditRatePercent: interestCredit * 100,
        targetCreditsById: Object.fromEntries(targets.map((row) => [row.id, row.cbCreditCents / 100])),
        nonTargetHceCbPercent,
        staffCbPercent: solved.staffCbBp / 100,
        // Group-shaped credits keyed by class LABEL — the atomic apply maps
        // labels to allocation-group ids inside the same store transaction.
        creditsByLabel: [
          ...targets.map((row) => ({
            label: targetLabels.get(row.id),
            method: 'flatDollar',
            value: row.cbCreditCents / 100,
          })),
          { label: hceLabel, method: 'percentOfPay', value: nonTargetHceCbPercent },
          { label: staffLabel, method: 'percentOfPay', value: solved.staffCbBp / 100 },
        ],
      },
      suggestedDesign: {
        type: 'grouped',
        groups: [
          ...targetPeople.map((p) => ({
            label: targetLabels.get(p.id),
            formula: solved.targetPsFill
              ? { method: 'fill_415c', percent: 0, amount: 0 }
              : { method: 'percent', percent: targetPsPercent, amount: 0 },
          })),
          { label: hceLabel, formula: { method: 'percent', percent: nonTargetHcePsPercent, amount: 0 } },
          { label: staffLabel, formula: { method: 'percent', percent: solved.staffPsBp / 100, amount: 0 } },
        ],
      },
      groupAssignments: Object.fromEntries(people.map((person) => [
        person.id,
        person.role === ROLE_TARGET ? targetLabels.get(person.id)
          : person.role === ROLE_NON_TARGET_HCE ? hceLabel
            : staffLabel,
      ])),
    }
  }

  // ---- Generate scenarios ----
  const scenarios = []
  const primary = solveDesign()
  if (primary.status === 'unavailable') {
    return { ...base, status: 'blocked', blockedStatus: makeComplianceStatus({ status: 'unavailable', reasons: [makeReason(primary.reason || 'unavailable', `Combined testing is unavailable: ${primary.reason}.`)] }) }
  }
  if (primary.status === 'infeasible') {
    return {
      ...base,
      status: 'infeasible',
      diagnostics: {
        bindingConstraint: primary.binding,
        message: primary.binding === '401a26'
          ? 'Even at the staff cash-balance cap, too few participants reach a meaningful DB benefit for §401(a)(26).'
          : primary.binding === 'top-heavy'
            ? 'Even at the staff profit-sharing cap, §415(c) headroom leaves non-key staff short of the §416 minimum for a DB/DC aggregation group.'
            : `Every staff profit-sharing rate within the cap triggers a combined-screen review flag (binding: ${primary.binding}).`,
      },
    }
  }
  scenarios.push(scenarioFrom('combo-lowest-cost', 'Lowest-cost combination without modeled flags', primary,
    `Owner credit at the §415(b) level solve, staff cash balance ${(primary.staffCbBp / 100).toFixed(2)}%, staff profit sharing ${(primary.staffPsBp / 100).toFixed(2)}% — the smallest DC rate without a modeled combined gateway or aggregate-test flag. This is not a formal determination.`))

  // 7.5% deemed-gateway posture (skip when the primary already required 7.5%).
  if ((primary.tests.requiredGatewayPercent ?? 0) < 7.5) {
    const deemed = solveDesign({ forceGatewayPercent: 7.5 })
    if (deemed.status === 'solved' && deemed.staffPsBp !== primary.staffPsBp) {
      scenarios.push(scenarioFrom('combo-deemed-7-5', '7.5% deemed-gateway posture', deemed,
        'Every NHCE at a 7.5% aggregate allocation — the deemed-satisfaction route, independent of the highest HCE rate, at a higher employer cost.'))
    }
  }

  // Reduced owner credit (75%): can drop the required gateway band and staff cost.
  const reduced = solveDesign({ targetCreditScaleBp: 7500 })
  if (reduced.status === 'solved') {
    scenarios.push(scenarioFrom('combo-reduced-owner-cb', 'Reduced owner credit (75%)', reduced,
      'Owner cash-balance credit at 75% of the §415(b) level solve — less owner benefit, and a lower aggregate gateway cost when it moves the required band.'))
  }

  // More through DC: owner CB at 50%, owner PS fills remaining §415(c) headroom.
  const moreDc = solveDesign({ targetCreditScaleBp: 5000, targetPsFill: true })
  if (moreDc.status === 'solved') {
    scenarios.push(scenarioFrom('combo-more-dc', 'More through the DC plan', moreDc,
      'Owner cash-balance credit at 50% with profit sharing filling the owner’s §415(c) headroom — a DC-weighted alternative with different deduction and funding character.'))
  }

  // Budget fit: scale the owner credit until total employer cost fits.
  const budgetCents = toCents(goal.budget)
  if (budgetCents > 0) {
    const costAt = (scaleBp) => {
      const solved = solveDesign({ targetCreditScaleBp: scaleBp })
      if (solved.status !== 'solved') return null
      const rows = buildRows(solved.staffCbBp, solved.staffPsBp, { targetCreditScaleBp: scaleBp })
      const cost = rows.reduce((sum, row) => sum + toCents(row.cbCredit) + toCents(row.psAmount)
        + toCents(row.nonelective) + toCents(row.qnec) + toCents(row.match), 0)
      return { solved, cost }
    }
    const full = costAt(10000)
    if (full && full.cost > budgetCents) {
      let lo = 0
      let hi = 10000
      while (hi - lo > 1 && evaluations < 380) {
        const mid = Math.floor((lo + hi) / 2)
        const at = costAt(mid)
        if (at && at.cost <= budgetCents) lo = mid
        else hi = mid
      }
      const fit = costAt(lo)
      if (fit && lo > 0) {
        scenarios.push(scenarioFrom('combo-budget-fit', 'Best owner credit inside the budget', fit.solved,
          `Owner credit scaled to ${(lo / 100).toFixed(2)}% of the §415(b) level solve — the largest owner benefit whose total employer cost fits the budget.`, { optimality: 'lowest-cost-found' }))
      }
    }
  }

  // Nondominated on (employer cost, owner benefit), defensive postures kept.
  const ownerBenefit = (s) => s.totals.targetCashBalanceCents + s.totals.targetProfitSharingCents
  const kept = scenarios.filter((scenario) => !scenarios.some((other) => other !== scenario
    && other.totals.employerCostCents <= scenario.totals.employerCostCents
    && ownerBenefit(other) >= ownerBenefit(scenario)
    && (other.totals.employerCostCents < scenario.totals.employerCostCents || ownerBenefit(other) > ownerBenefit(scenario))))
  for (const scenario of scenarios) {
    if (!kept.includes(scenario) && scenario.id === 'combo-deemed-7-5') kept.push(scenario)
  }
  kept.sort((a, b) => a.totals.employerCostCents - b.totals.employerCostCents || a.id.localeCompare(b.id))

  return {
    ...base,
    status: 'solved',
    scenarios: kept.slice(0, 5),
    assumptions: {
      summary: [
        ...(primary.tests.assumptions?.summary || []),
        ...limits415bAssumptions(),
        'Owner credits solve the level end-of-year pay credit reaching the §415(b) maximum lump sum at retirement (plan-basis APR).',
        'Staff percentages solved deterministically to the basis point; suggested owner credits round the level credit up to the next $1,000.',
      ],
      factorsUsed: primary.tests.assumptions?.factorsUsed || [],
      estimated: primary.tests.assumptions?.estimated || [],
    },
  }
}

export { requiredAggregateGatewayPercent }
